import { useAppDispatch } from '@/hooks/use-app-dispatch'
import { useAppSelector } from '@/hooks/use-app-selector'
import { updateLayout } from '@/states/verticallySplitLayout.slice'
import { CloseFullscreen, OpenInFull } from '@mui/icons-material'
import { Box, Card, CardContent, CardHeader, IconButton } from '@mui/material'
import React, { ReactElement, useEffect, useState } from 'react'

export type IVerticallySplitLayout = {
  headerTop?: ReactElement
  headerBottom?: ReactElement
  contentTop?: ReactElement
  contentBottom?: ReactElement
}
const VerticallySplitLayout: React.FC<IVerticallySplitLayout> = ({
  headerTop,
  contentTop,
  headerBottom,
  contentBottom,
}) => {
  const dispatch = useAppDispatch()
  const layout = useAppSelector((s) => s.verticallySplitLayout?.contentFocus)
  const [displayExpandButton, setDisplayExpandButton] = useState(
    contentTop && contentBottom,
  )

  useEffect(() => {
    // If Content Top is Available and Content Bottom is Not available, Only show Content Top
    if (contentTop && !contentBottom) {
      expandTopContainer()
    }
    // If Content Top is Not Available and Content Bottom is available, Only show Content Bottom
    if (!contentTop && contentBottom) {
      expandBottomContainer()
    }
  }, [contentTop, contentBottom])

  useEffect(() => {
    setDisplayExpandButton(contentTop && contentBottom)
  }, [layout, contentTop, contentBottom])

  useEffect(() => {
    resetLayout()
  }, [])

  const expandTopContainer = () => {
    dispatch(
      updateLayout({
        isBottomDisplayed: false,
        isBottomExpanded: false,
        isTopDisplayed: true,
        isTopExpanded: true,
      }),
    )
  }

  const expandBottomContainer = () => {
    dispatch(
      updateLayout({
        isBottomDisplayed: true,
        isBottomExpanded: true,
        isTopDisplayed: false,
        isTopExpanded: false,
      }),
    )
  }

  const resetLayout = () => {
    dispatch(
      updateLayout({
        isBottomDisplayed: true,
        isBottomExpanded: false,
        isTopDisplayed: true,
        isTopExpanded: false,
      }),
    )
  }

  return (
    <Box
      display='grid'
      gridTemplateRows={`repeat(${layout?.isTopDisplayed && layout?.isBottomDisplayed ? 2 : 1}, minmax(auto, 1fr))`}
      gap={4}
    >
      {layout?.isTopDisplayed && (
        <Card>
          {headerTop && (
            <CardHeader
              title={headerTop}
              action={
                displayExpandButton && (
                  <Box
                    display='grid'
                    alignItems='center'
                    justifyContent='center'
                  >
                    <IconButton
                      className='chart-expand'
                      size='small'
                      onClick={() =>
                        layout?.isTopExpanded
                          ? resetLayout()
                          : expandTopContainer()
                      }
                    >
                      {layout?.isTopExpanded ? (
                        <CloseFullscreen fontSize='inherit' />
                      ) : (
                        <OpenInFull fontSize='inherit' />
                      )}
                    </IconButton>
                  </Box>
                )
              }
            />
          )}
          <CardContent
            sx={{
              height: 'calc(100% - 50px)',
              pt: headerTop ? 0 : 4,
              boxSizing: 'border-box',
            }}
          >
            {contentTop}
          </CardContent>
        </Card>
      )}
      {layout?.isBottomDisplayed && (
        <Card sx={{ height: '100%' }}>
          <CardHeader
            title={headerBottom}
            action={
              displayExpandButton && (
                <Box>
                  <IconButton
                    className='table-expand'
                    onClick={() =>
                      layout?.isBottomExpanded
                        ? resetLayout()
                        : expandBottomContainer()
                    }
                    size='small'
                  >
                    {layout?.isBottomExpanded ? (
                      <CloseFullscreen fontSize='inherit' />
                    ) : (
                      <OpenInFull fontSize='inherit' />
                    )}
                  </IconButton>
                </Box>
              )
            }
          />
          <CardContent
            sx={{
              height: 'calc(100% - 50px)',
              paddingTop: 0,
              boxSizing: 'border-box',
            }}
          >
            {contentBottom}
          </CardContent>
        </Card>
      )}
    </Box>
  )
}

export default VerticallySplitLayout
